export interface User {
  id: string;
  name: string;
  email?: string; // Added optional email field
  is_admin: boolean;
  member_type: 'adult' | 'child';
  /** A child's age group, e.g. 'age_6_7' (2026-10-15; used to suggest tasks). */
  age_group?: string | null;
  points: number;
  avatar_url?: string | null;
  coins?: number;
  // Temporary: still accept stars in UI until all references updated
  stars?: number;
  has_google_identity?: boolean;
  password: string; // stored in local storage for demo; replace with hash/server later
  username?: string;
  /** Only on current_user: the family is waiting to be deleted (see FamilyDeletionPending). */
  family_deletion?: FamilyDeletion | null;
}

export interface Room {
  id: string;
  name: string;
  original_name: string;
  /** Optional picture of the room, so children recognise it (2026-10-13). */
  image_url?: string | null;
}

export interface Tool {
  id: string;
  name: string;
  description?: string | null;
  /** Localized names from catalog or custom tool */
  name_i18n?: Record<string, string> | null;
  description_i18n?: Record<string, string> | null;
  image?: string; // image ID stored in IndexedDB via ImageService
  created_at: Date;
  // New optional fields when present
  tool_key?: string;
  tool_type?: 'tool' | 'supply';
  is_consumable?: boolean;
  image_url?: string;
  /** When true, global catalog sync does not overwrite this family row */
  customized?: boolean;
}

export interface ChecklistItem {
  id: string;
  text: string;
  images: string[]; // image IDs stored in IndexedDB via ImageService
  /** Tools this step needs (family tool ids), shown to the child under the step (2026-10-13). */
  tool_ids?: string[];
  /** Timed step (2026-10-19): seconds for the on-screen timer, or none. */
  timer_seconds?: number | null;
}

export interface TaskChecklistState {
  [item_id: string]: boolean; // true when item is checked by the assignee
}

export interface Task {
  id: string;
  title: string;
  description?: string;
  room_id: string | null;
  points: number;
  checklist: ChecklistItem[];
  tools: string[]; // array of tool IDs
  checklist_state?: TaskChecklistState;
  is_completed: boolean; // submitted by assignee
  verified?: boolean; // approved by admin
  verified_by?: string; // id of the admin who verified it
  verified_at?: Date;
  /** Set once the completing member has been shown the verification celebration message. */
  verification_seen_at?: Date;
  assigned_to?: string;
  completed_by?: string;
  completion_message?: string;
  created_at: Date;
  completed_at?: Date;
  due_date?: Date; // specific date when task should be completed
  recurrence_days?: number; // recreate every X days after verification
  recreate_on_validation?: boolean; // if true, clone task on admin validation
  days_to_complete?: number; // due date offset in days from creation
  auto_available?: boolean; // if true, recurring tasks are automatically available
  is_available?: boolean; // if true, task is available for claiming
  header_image?: string; // ID of the header image for the task
  /** Public image URL when set (e.g. from a standard task); shown instead of IndexedDB header_image */
  header_image_url?: string | null;
  /** All copies of a repeating task share one series (stats count them together). */
  series_id?: string | null;
  age_group?: 'all_ages' | 'age_2_3' | 'age_4_5' | 'age_6_7' | 'age_8_9' | 'age_10_12' | 'age_13_plus' | 'adults';
  /** Set when verify/repair recorded a payout (ISO string from API). */
  points_awarded_on_verification?: string | null;
  /** Set after one-time misattribution repair (ISO string from API). */
  misattributed_payout_repair_at?: string | null;
  available_from?: string | null;
  /** Chains (2026-10-13): the next step's series, ready this many hours after this one is done. */
  next_series_id?: string | null;
  next_wait_hours?: number | null;
  /** Waiting for the step before it to be approved (hidden from the children). */
  is_waiting?: boolean;
  /** Shared tasks (2026-10-14): every person's copy of one task shares this id. */
  share_group_id?: string | null;
  /** A parent's edit waiting for this copy's next round. */
  pending_update?: any;
  /** Fixed plan, e.g. every Thursday (2026-10-14; see utils/repeatSchedule). */
  repeat_schedule?: any;
  /** "Gives pocket money": this task's stars count towards pocket money (default true). */
  counts_for_pocket_money?: boolean;
  approval_note?: 'so_neat' | 'well_done' | 'thank_you' | null;
  not_done_reason?: 'look_again' | 'steps_missing' | 'do_together' | null;
}

export interface Family {
  id: string;
  name: string;
  admin_id: string;
  onboarding_completed_at?: string | null;
  members: User[];
  rooms: Room[];
  tasks: Task[];
  rewards: Reward[];
  tools: Tool[]; // global list of available tools
  /** Total verified (finished) tasks in the family — `tasks` only holds the most recent ones. */
  completed_total?: number | null;
  /** True when older verified tasks exist that haven't been loaded (see loadMoreCompletedTasks). */
  completed_has_more?: boolean;
  /** Stats Family tab for children (docs/design/family-stats.md, D1). */
  stats_visibility?: 'team' | 'full' | 'admins';
  /** Grown-ups' tasks wait for another grown-up's approval (2026-10-19); off = they approve their own. */
  adults_need_approval?: boolean;
  /** Members an admin deleted, restorable until permanent_at (account deletion, 2026-10-12). Admins only. */
  deleted_members?: DeletedMember[];
  /** Payments groundwork; only used when FEATURES.payments is on. */
  billing?: FamilyBilling | null;
  /** Pocket money is on for at least one child (2026-10-14). */
  pocket_money_enabled?: boolean;
}

/** Access/billing state from get-family (supabase/functions/_shared/billing.ts). */
export interface FamilyBilling {
  state: 'exempt' | 'active' | 'grace' | 'cancelling' | 'trial' | 'expired';
  hasAccess: boolean;
  plan: 'monthly' | 'yearly' | null;
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
  trialEndingSoon: boolean;
  until: string | null;
  renewsAt: string | null;
}

export interface DeletedMember {
  id: string;
  name: string;
  avatar_url?: string | null;
  member_type: 'adult' | 'child';
  deleted_at: string;
  /** When the member and all their data are gone for good. */
  permanent_at: string;
}

/** Set on current_user while the whole family is waiting to be deleted (it can be restored). */
export interface FamilyDeletion {
  family_name: string;
  requested_at: string;
  permanent_at: string;
}

export interface AppState {
  current_user: User | null;
  current_family: Family | null;
  is_authenticated: boolean;
}

export interface Reward {
  id: string;
  title: string;
  description: string;
  points_required: number;
  target_type: 'individual' | 'family';
  target_id?: string; // family member ID for individual rewards, undefined for family rewards
  is_claimed: boolean;
  claimed_by?: string; // user ID who claimed the reward
  claimed_at?: Date;
  /** Set when a grown-up has handed a claimed reward over (null = still to hand over). */
  handed_over_at?: string | null;
  handed_over_by?: string | null;
  created_at: Date;
  header_image?: string; // uploaded picture URL (since 2026-10-13) or an old local image id
  /** Points count toward this reward only from this instant (ISO from server). */
  progress_start_at?: string;
  /** Sum of point_ledger deltas for this reward’s window (from server). */
  eligible_points?: number;
}

// API Request/Response types
export interface CreateTaskRequest {
  title: string;
  description?: string;
  room_id?: string;
  points: number;
  header_image?: string;
  header_image_url?: string | null;
  checklist: Array<{ text: string; order_index: number; images?: string[]; tool_ids?: string[]; timer_seconds?: number | null }>;
  tools?: string[];
  due_date?: string;
  recurrence_days?: number;
  recreate_on_validation?: boolean;
  days_to_complete?: number;
  auto_available?: boolean;
  next_series_id?: string | null;
  next_wait_hours?: number | null;
  /** Shared tasks: two or more people, each gets their own copy. */
  assignees?: string[];
  repeat_schedule?: any;
  counts_for_pocket_money?: boolean;
  assigned_to?: string; // ID of the family member to assign the task to
  age_group?: 'all_ages' | 'age_2_3' | 'age_4_5' | 'age_6_7' | 'age_8_9' | 'age_10_12' | 'age_13_plus' | 'adults';
}

export interface UpdateTaskRequest {
  title?: string;
  description?: string;
  room_id?: string;
  points?: number;
  header_image?: string;
  header_image_url?: string | null;
  checklist?: Array<{ text: string; order_index: number; images?: string[]; tool_ids?: string[]; timer_seconds?: number | null }>;
  tools?: string[];
  due_date?: string;
  recurrence_days?: number;
  recreate_on_validation?: boolean;
  days_to_complete?: number;
  auto_available?: boolean;
  next_series_id?: string | null;
  next_wait_hours?: number | null;
  /** Shared tasks: two or more people, each gets their own copy. */
  assignees?: string[];
  repeat_schedule?: any;
  counts_for_pocket_money?: boolean;
  assigned_to?: string; // ID of the family member to assign the task to
  age_group?: 'all_ages' | 'age_2_3' | 'age_4_5' | 'age_6_7' | 'age_8_9' | 'age_10_12' | 'age_13_plus' | 'adults';
}

// Utility function to safely convert date strings to Date objects
/**
 * Most task/reward times are stored as "timestamp without time zone" holding UTC, so the server
 * sends e.g. "2026-10-20T18:00:00" with no zone; JavaScript would read that as local time and show
 * deadlines 1–2 hours early in Denmark (2026-10-13). Such strings are read as UTC.
 */
const NAIVE_DATE_TIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

export const safeDate = (date: Date | string | undefined): Date | undefined => {
  if (!date) return undefined;
  if (date instanceof Date) return date;
  return new Date(NAIVE_DATE_TIME.test(date) ? `${date.replace(' ', 'T')}Z` : date);
};



